/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from "react";
import { Box } from "@mui/material";
import { DashboardProvider } from "src/context/dashboard";
import { FigureCards } from "src/components/pages/dashboard";
import ChatbotFigureCards from "src/components/pages/dashboard/ChatbotFigureCards";
import SecondLayer from "src/components/pages/dashboard/SecondLayer";
import ThirdLayer from "src/components/pages/dashboard/ThirdLayer";
import FourthLayer from "src/components/pages/dashboard/FourthLayer";
import { useFeature } from "src/context/global";
import { useTour } from "src/context/global/TourContext";
import Tour from "src/components/shared/molecules/Tour/Tour";
import { DASHBOARD_TOUR } from "src/config/tourSteps";
import { CallBackProps } from "react-joyride";
import { useMutation } from "@apollo/client";
import UPDATE_USER_GUIDE from "src/graphql/mutation/user_guide/updateUserGuide";
import GET_USER_GUIDE from "src/graphql/query/user_guide/getUserGuide";
import { useSelector } from "react-redux";
import useActiveUserDetail from "src/utils/shared/useActiveUserDetail";
import TourGuidanceModal from "src/components/shared/molecules/TourGuidanceModal";
import FacebookConnectionModal from "src/components/shared/molecules/FacebookConnectionModal";
import GlobalTimeFrameFilter from "src/components/pages/dashboard/GlobalTimeFrameFilter";
import useStyles from "./Styles";

const MODAL_DELAY = 3000;

const Dashboard: React.FC = () => {
  const { classes } = useStyles();
  const { canUse } = useFeature();
  const { me }: any = useSelector((state: any) => state?.me || {});
  const { panelType } = useActiveUserDetail();
  const { guideData, dashboardTourRun, startDashboardTour, stopDashboardTour } =
    useTour();
  const [showModal, setShowModal] = useState(false);
  const [showConnectionModal, setShowConnectionModal] = useState(false);

  const [updateUserGuide] = useMutation(UPDATE_USER_GUIDE);

  // ✅ check permissions
  const canViewThirdLayer = canUse("graphical_last_7_days_data");
  const canViewFourthLayer = canUse("pages_wise_channels_wise_breakdown");

  // Filter tour steps based on permissions and role
  const filteredSteps = useMemo(() => {
    return DASHBOARD_TOUR.filter((step) => {
      // Skip Dashboard Users step for agents
      if (step.target === "#tour-dashboard-users" && panelType === "AGENT") {
        return false;
      }
      // Skip Graphical Chart step if no permission
      if (
        step.target === "#tour-dashboard-graphical-chart" &&
        !canViewThirdLayer
      ) {
        return false;
      }
      // Skip Pages Breakdown step if no permission
      if (
        step.target === "#tour-dashboard-pages-breakdown" &&
        !canViewFourthLayer
      ) {
        return false;
      }
      return true;
    });
  }, [panelType, canViewThirdLayer, canViewFourthLayer]);

  // Auto-start modal logic if welcome tour is done but dashboard tour isn't
  // useEffect(() => {
  //   if (!guideData?.getUserGuide) return;

  //   if (
  //     guideData.getUserGuide.welcomeTourCompleted &&
  //     !guideData.getUserGuide.dashboardTourCompleted
  //   ) {
  //     setShowModal(true);
  //   }

  //   if (
  //     guideData.getUserGuide.dashboardTourCompleted &&
  //     !guideData.getUserGuide.channelConnected
  //   ) {
  //     setShowConnectionModal(true);
  //   }
  // }, [guideData]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    // Only show these setup/guide modals to Superadmins/Admins
    if (panelType === "SUPERADMIN") {
      // 🛑 Delay onboarding until profile is complete (email registered + verified)
      const isProfileIncomplete =
        !me?.email || ((me?.facebookId || me?.instagramId) && !me?.verified);

      if (isProfileIncomplete) {
        setShowModal(false);
        setShowConnectionModal(false);
      } else {
        if (
          (!guideData || !guideData?.getUserGuide?.dashboardTourCompleted) &&
          !dashboardTourRun
        ) {
          timer = setTimeout(() => {
            setShowModal(true);
          }, MODAL_DELAY);
        }

        if (
          guideData &&
          guideData?.getUserGuide?.dashboardTourCompleted &&
          !dashboardTourRun &&
          !guideData?.getUserGuide?.channelConnected
        ) {
          timer = setTimeout(() => {
            setShowConnectionModal(true);
          }, MODAL_DELAY);
        } else {
          setShowConnectionModal(false);
        }
      }
    } else {
      // Ensure modals are closed for non-admins
      setShowModal(false);
      setShowConnectionModal(false);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [guideData, dashboardTourRun, panelType, me]);

  const handleAgree = async () => {
    if (me?.id) {
      try {
        await updateUserGuide({
          variables: {
            agentId: me.id,
            dashboardTourCompleted: true,
          },
          refetchQueries: [
            { query: GET_USER_GUIDE, variables: { agentId: me.id } },
          ],
        });
        setShowModal(false);
        startDashboardTour();
      } catch (error) {
        console.error("Failed to mark dashboard tour as completed:", error);
      }
    }
  };

  const handleDismiss = async () => {
    setShowModal(false);
    if (me?.id) {
      try {
        await updateUserGuide({
          variables: {
            agentId: me.id,
            dashboardTourCompleted: true,
          },
          refetchQueries: [
            { query: GET_USER_GUIDE, variables: { agentId: me.id } },
          ],
        });
      } catch (error) {
        console.error("Failed to mark dashboard tour as completed:", error);
      }
    }
  };

  const handleTourCallback = async (data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = ["finished", "skipped"];

    if (finishedStatuses.includes(status)) {
      stopDashboardTour();
    }
  };

  return (
    <DashboardProvider>
      <Box className={classes.root}>
        <GlobalTimeFrameFilter />
        <FigureCards />
        <ChatbotFigureCards />
        <SecondLayer />

        {canViewThirdLayer && <ThirdLayer />}
        {canViewFourthLayer && <FourthLayer />}
      </Box>

      <Tour
        steps={filteredSteps}
        run={dashboardTourRun}
        callback={handleTourCallback}
        continuous
      />

      <TourGuidanceModal
        open={showModal}
        onClose={handleDismiss}
        onAgree={handleAgree}
        title="Dashboard Guide"
        content="Do you want to see the dashboard user guide?"
      />

      <FacebookConnectionModal
        open={showConnectionModal}
        onClose={() => setShowConnectionModal(false)}
      />
    </DashboardProvider>
  );
};

export default Dashboard;
